<template>
  <div class="e2bpdoc">
    <h1>Manual sobre el Excel de BP</h1>

    <p>Actualizado 2026-01-27</p>

    <v-divider class="e2bpdoc-separator"></v-divider>

    <h2>Estructura general del Excel</h2>

    <p>
      Las páginas que va a leer el conversor van siempre identificadas con un código en la primera celda (<code>A1</code>). Este código indica al conversor que información contiene la página. Por ello, <strong>es possible reordenar y renombrar las hojas</strong> siempre que se mantenga intacto el valor de la primera celda. También se pueden crear nuevas hojas con información para personas (la máquina las va a ignorar), como "NORMA", o "GRÁFICA", pensadas para facilitar la comprensión del Excel a quien lo lea pero que no son relevantes para el conversor.
    </p>

    <p>Los típos de páginas que sabe leer el conversor son, por su valor en la primera celda:</p>

    <ul>
      <li><strong>@bpmeta</strong> → Configuración general sobre el BP y su típo</li>
      <li><strong>@model</strong> → Definición del modelo de datos / campos</li>
      <li><strong>@states</strong> → Lista de los estados de BP</li>
      <li><strong>@notifications</strong> → Mensajes de notificaciones identificados por un ID</li>
      <li><strong>@transitions</strong> → Lista de transiciones con código y nombre</li>
      <li><strong>@state_jumps</strong> → Reglas de transición entre los estados del BP</li>
      <li><strong>@table</strong> → Tabla del explorer de QmKey</li>
      <li><strong>@modals</strong> → Definiciones de los modales de cada transición</li>
    </ul>

    <v-divider class="e2bpdoc-separator"></v-divider>

    <h2>BP Meta</h2>

    <p>Chuleta con los valores que se pueden configurar en @bpmeta. Cada <i>clave</i> ("BP", "Proceso", "Tipo"...) va a una fila empezando por la celda <code>A2</code> (inclusive), seguido de su valor en la siguiente columna. Por ejemplo, en el caso de <code>A2</code>, su valor va en <code>B2</code>.</p>

    <p><strong>¡Importante!</strong> Las <i>claves</i> deben coincidir letra a letra con los de la siguiente tabla. No distingue entre mayúsculas y minúsculas ("BP" = "Bp" = "bp")</p>

    <table>
      <tr>
        <th>@bpmeta (Clave)</th>
        <th>Valor / Castellano</th>
        <th>Català</th>
        <th>English</th>
        <th>Comentario sobre como se usa</th>
      </tr>

      <tr>
        <td>BP</td>
        <td>REFERENCIA_BP</td>
        <td></td>
        <td></td>
        <td>Escribir en mayúsculas y usar "_" en lugar de espacios</td>
      </tr>

      <tr>
        <td>Proceso</td>
        <td>REFERENCIA_BP</td>
        <td></td>
        <td></td>
        <td>Preferentemente usar el mismo valor que BP</td>
      </tr>

      <tr>
        <td>Tipo</td>
        <td>
          <span>Debe ser uno de estos:</span>
          <ul>
            <li>Expediente</li>
            <li>Seguimiento</li>
          </ul>
        </td>
        <td></td>
        <td></td>
        <td>Viene puesto en la plantilla, mejor elige la plantilla correcta</td>
      </tr>

      <tr>
        <td>Planificaciones</td>
        <td>SEGIMIENTODEFAULT_V2</td>
        <td></td>
        <td></td>
        <td>Opcional. Múltiples valores: SEG1, SEG2</td>
      </tr>

      <tr>
        <td>Nombre</td>
        <td>Nombre del BP</td>
        <td>Nóm del BP</td>
        <td>BP's name</td>
        <td>Obligatorio el valor en castellano</td>
      </tr>

      <tr>
        <td>Descripción</td>
        <td>Descripción del BP</td>
        <td>Descripció del BP</td>
        <td>BP's description</td>
        <td>Opcional. Idealmente deberia ser una o dos frases</td>
      </tr>
    </table>

    <v-divider class="e2bpdoc-separator"></v-divider>

    <h2>Modelo de datos</h2>

    <p>La información de los campos empieza en <code>A3</code> inclusive. Cada fila es un campo.</p>

    <p>La <strong>columna ID</strong> asigna un código identificativo al campo, y este ID es el que se usará para referenciar el campo en las otras páginas del Excel. Puede usarse cualquier texto, pero se recomienda el siguiente patrón: <code>fecha limite</code> en minúsuclas y sin acentos.</p>

    <table>
      <thead>
        <tr>
          <th>Típo</th>
          <th>Subtipo</th>
          <th>Valor predeterminado</th>
          <th>Otros valores</th>
          <th>Comentario sobre el campo</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Referencia</td>
          <td>REF-#####</td>
          <td></td>
          <td></td>
          <td>Usar "#" para los dígitos. Ejemplos: <code>NC-#####</code>, <code>ABC#####DEF</code></td>
        </tr>

        <tr>
          <td>Text</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

        <tr>
          <td rowspan="4">Select</td>
          <td>proceso</td>
          <td></td>
          <td></td>
          <td>Selector de procesos</td>
        </tr>
        <tr>
          <td rowspan="2">user</td>
          <td>@user</td>
          <td></td>
          <td>Selector de los usuarios del centro</td>
        </tr>
        <tr>
          <td>@user</td>
          <td>@permiso revisor</td>
          <td>Selector de usuarios que otorga el permiso que se ponga</td>
        </tr>
        <tr>
          <td></td>
          <td></td>
          <td>Etiqueta1, Mi otra etiqueta</td>
          <td>Los vocabularios se definen automaticamente</td>
        </tr>
        
        <tr>
          <td>Fecha</td>
          <td></td>
          <td>@hoy +2d</td>
          <td></td>
          <td>Por defecto usa la fecha de hoy. Solo se puede sumar o restar días</td>
        </tr>
        <tr>
          <td>Textarea</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>Image</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>File</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>Relacion</td>
          <td>REFERENCIA_BP</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>Numeric</td>
          <td></td>
          <td></td>
          <td>@rango 0-9999999</td>
          <td>Valores negativos (-100 ↔ +100): @rango -100-100. Máx debe ser >= 0</td>
        </tr>
        <tr>
          <td>Decimal</td>
          <td>2 decimales</td>
          <td></td>
          <td>@rango 0-9999999</td>
          <td>El rango debe ser un valor sin decimales</td>
        </tr>
      </tbody>
    </table>

    <p></p>
    <h3>Campos siempre presentes</h3>

    <p>Campos que deben existir si o si. <strong>El conversor los va a definir correctamente</strong> en caso de no estar presentes en la página del Modelo de datos. Por ello pueden usarse siempre en otrás páginas como "Modales", "Tabla"... Si se necesita definir algúna propiedad especial, se puede añadir al modelo de datos y cambiar las propiedades al gusto (por ejemplo para cambiar el código de referencia)</p>
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>reference</td>
          <td>Referencia, solo se puede definir uno. Debe ser de tipo <code>Referencia</code></td>
        </tr>
        <tr>
          <td>title</td>
          <td>Título del expediente. Debe ser de tipo <code>Text</code></td>
        </tr>
        <tr>
          <td>descripcion</td>
          <td>Debe ser de tipo <code>Textarea</code></td>
        </tr>
        <tr>
          <td>affected process</td>
          <td>Selector de procesos</td>
        </tr>
        <tr>
          <td>fecha creacion</td>
          <td>Campo de fecha. Recuerda ponerlo en el modal de creación</td>
        </tr>
        <tr>
          <td>responsable</td>
          <td>Campo de usuario, otorga permiso de lectura</td>
        </tr>
        <tr>
          <td>creado por</td>
          <td>Campo de usuario. Recuerda ponerlo en el modal de creación</td>
        </tr>
        <tr>
          <td>cerrado por</td>
          <td>Campo de usuario que cierra el expediente</td>
        </tr>
        <tr>
          <td>fecha cierre</td>
          <td>Fecha de cierre del expediente</td>
        </tr>
        <tr>
          <td>motivo reapertura</td>
          <td>Motivo de la reapertura. Campo de texto</td>
        </tr>
        <tr>
          <td>motivo eliminacion</td>
          <td>Motivo de la eliminación. Campo de texto</td>
        </tr>
        <tr>
          <td>eliminado por</td>
          <td>Campo de usuario que elimina el expediente</td>
        </tr>
        <tr>
          <td>fecha eliminacion</td>
          <td>Fecha de eliminación del expediente</td>
        </tr>
      </tbody>
    </table>

    <p></p>
    <h3>Campos fijos</h3>
    <p>Esta es la lista de campos <strong>fijos</strong> que se pueden usar para leer su valor (por ejemplo en la Tabla), pero <strong>no se pueden modificar</strong>, y por ello no deben ponerse en la página de Modelo de datos del Excel. Son manejados única y exclusivamente por el sistema</p>

    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>current workflow state</td>
          <td>Estado actual del expediente</td>
        </tr>
        <tr>
          <td>current workflow limit</td>
          <td>Fecha límite</td>
        </tr>
        <tr>
          <td>current workflow responsible</td>
          <td>Usuario responsable (le saldrá en el todo-list)</td>
        </tr>
        <tr>
          <td>current workflow supervisor</td>
          <td>Usuario supervisor actual</td>
        </tr>
        <tr>
          <td>creation date</td>
          <td>Fecha de creación del expediente. Fecha real del sistema, no modificable</td>
        </tr>
        <tr>
          <td>end date</td>
          <td>Fecha de finalización del expediente. Fecha real del sistema, no modificable</td>
        </tr>
        <tr>
          <td>delete date</td>
          <td>Fecha de borrado del expediente. Fecha real del sistema, no modificable</td>
        </tr>
        <tr>
          <td>delete user</td>
          <td>Usuario que realizó el borrado. Asignado por el sistema.</td>
        </tr>
        <tr>
          <td>updated date</td>
          <td>Fecha de la última transición. Fecha real del sistema, no modificable</td>
        </tr>
        <tr>
          <td>centro field</td>
          <td>Centro asociado al expediente. Asignado por el sistema</td>
        </tr>
        <tr>
          <td>default relationship</td>
          <td>Campo interno de relaciones. Manejado por el sistema</td>
        </tr>
      </tbody>
    </table>

    <v-divider class="e2bpdoc-separator"></v-divider>

    <h2>Modales</h2>

    <p>Cada fila debe tener los campos de esa fila. No se deben definir en dos linias</p>
    <p>Para indicar algúna propiedad especial del campo en ese modal, usa los siguientes carácteres:</p>

    <pre>
Nombre*	→ Obligatorio
Nombre$	→ No editable
Nombre&	→ Oculto
(texto)	→ Ignorado
    </pre>

    <p>Se pueden combinar múltiples propiedades: <code>Nombre*$</code> indica que <i>Nombre</i> es obligatorio y no editable a la vez</p>

  </div>
</template>

<style lang="scss">
 @import "@/sass/variables.scss";

 .e2bpdoc {
   margin-top: 8px;
 }

 .e2bpdoc-separator {
   margin: 16px 0px 16px 0px;
 }

 .e2bpdoc h1, .e2bpdoc h2 {
   margin-top: 18px;
   margin-bottom: 12px;
 }

 .e2bpdoc p {
   min-width: 320px;
   max-width: 1000px;
   text-align: justify;
 }

 .e2bpdoc table {
   background-color: #cacaca;
 }
 .e2bpdoc td, .e2bpdoc th {
   border-style: solid;
   border-width: 1px;
   border-color: transparent;
   margin: 0;
   padding: 6px;
   background-color: $background;
 }
 .e2bpdoc th {
   background-color: $primary;
   color: white;
 }
</style>

<script>
 import customThemes from '@/plugins/themes.js'
 import { mapState } from 'vuex'

 export default {
   name: "Excel2BPDocumentation",
   computed: {
   },
   
 };
</script>
